import { useEffect, useMemo, useState } from "react";
import ReactTable from 'react-table';
import { AddUpdateCountryCodeBlockModal, BackButton, BadgeComponent, Breadcrumbs, ButtonComponent, ConfirmationModalComponent, DropDownComponent, IconButtonComponent, LoaderComponent, PaginationComponent } from "../../components";
import { PlusSvg, VerticalDotsSvg } from "../../assets/svgs";
import { useDispatch, useSelector } from "react-redux";
import { AuthPageContainer, AuthPageHead, ReactTableWrapper, ReactTableWrappers } from "../../styles/global-styles";
import { getAllAvailableCountryCodeList, getAllCountryBlockList, postAddUpdateCountryCodeBlock } from "../../redux/slices/global-country-code-block-slice";
import { useLocation } from "react-router";
import { Menus } from "../../constants/menus-constant";
import flagSvgs from "../../helpers/importFlagSvgs";

const GlobalCountryBlock = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { loading, countryCodeBlockData, pagination } = useSelector((state) => state.globalCountryCodeBlockReducer);

    const MenuList = Menus({ user });

    const { label: PageLabel, icon: PageIcon } = MenuList?.flatMap(i => i?.subMenus)?.filter(i => i?.to === location?.pathname)[0];

    const initialPagination = {
        currentPage: 0,
        pageSize: 20,
    };

    const initialOrderBy = {
        sortColumn: "createdDate",
        isAsce: false,
    };

    const initialSearch = {
        countryName: "",
        numericCode: "",
        createdDate: "",
        isParticipantLevelBlocked: false,
    };

    const initialForm = {
        institutionCode: null,
        countries: [],
        effectiveDate: '',
    };

    const initialStates = {
        data: [],
        pagination: initialPagination,
        orderBy: initialOrderBy,
        search: initialSearch,
        selected: null,
        isDropdownOpen: false,
        isModalOpen: false,
        isConfirmModal: false,
        isUpdate: false,
        isView: false,
        isFiltersOpen: false,
    };

    const [states, setStates] = useState(initialStates);

    const [form, setForm] = useState(initialForm);

    const defaultParams = { pagination: states.pagination, orderBy: states.orderBy, search: states.search };

    const getAllBlockedCountryCodeData = async (updatedParams) => {
        if (user && updatedParams) {
            try {
                await Promise.all([
                    dispatch(getAllAvailableCountryCodeList({ user, search: { institutionCode: null } })),
                    dispatch(getAllCountryBlockList({ user, ...updatedParams })),
                ]);
            } catch (error) {
                console.error("One or more API calls failed", error);
            }
        }
    };

    // const handleReload = () => {
    //     getAllCountryBlockList(defaultParams);
    //     handleCloseFilters();
    // };

    const handlePagination = (e) => {
        const params = {
            ...defaultParams,
            pagination: {
                ...defaultParams.pagination,
                currentPage: e
            }
        };
        getAllBlockedCountryCodeData(params);
    };

    // const handleChangeFilters = (e) => {
    //     const { name, value, type, checked } = e.target
    //     setStates((old) => ({
    //         ...old,
    //         search: ({
    //             ...old.search,
    //             [name]: ['checkbox', 'radio'].includes(type) ? checked : value
    //         })
    //     }));
    // };

    const handleSelectRow = (row) => {
        if (row === states.selected && states.isDropdownOpen) {
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null }));
        } else {
            setStates((old) => ({ ...old, isDropdownOpen: true, selected: row }));
        }
    };

    const handleCloseDropdown = () => {
        setStates((old) => ({ ...old, isDropdownOpen: false }));
    };

    // const handleToggleFilters = () => {
    //     setStates((old) => ({ ...old, search: initialSearch, isFiltersOpen: !states.isFiltersOpen }));
    // };

    // const handleCloseFilters = () => {
    //     setStates((old) => ({ ...old, search: initialSearch, isFiltersOpen: false }));
    // };

    const handleModalOpen = async (e) => {
        if (['remove'].includes(e)) {
            setStates((old) => ({ ...old, isConfirmModal: !states.isConfirmModal, isModalOpen: false }));
        } else {
            try {
                await Promise.resolve(
                    dispatch(getAllAvailableCountryCodeList({ user, search: { institutionCode: null } })),
                );
            } finally {
                setStates((old) => ({ ...old, isModalOpen: true, isConfirmModal: false }));
                setForm(initialForm);
            }
        }
    };

    const handleModalClose = () => {
        setStates((old) => ({ ...old, isModalOpen: false, isConfirmModal: false, selected: null, isUpdate: false, isView: false }));
    };

    const handleFormSubmit = (payload) => {
        if (user && payload) {
            dispatch(postAddUpdateCountryCodeBlock({ user, body: payload, update: states.isUpdate })).then((action) => {
                if (action?.payload?.status === 201 || action?.payload?.status === 200) {
                    handleModalClose();
                    getAllBlockedCountryCodeData(defaultParams);
                }
            });
        }
    };

    const handleRemoveSubmit = () => {
        if (user && states.selected) {
            const updatedBody = {
                institutionCode: null,
                countries: [states.selected?.country],
                changeStatus: true,
                effectiveDate: states.selected?.effectiveDate,
            };

            dispatch(postAddUpdateCountryCodeBlock({ user, body: updatedBody })).then((action) => {
                if (action?.payload?.status === 201 || action?.payload?.status === 200) {
                    handleModalClose();
                    getAllBlockedCountryCodeData(defaultParams);
                }
            });
        }
    };

    const handleFiltersSubmit = (e, customSearch = null) => {
        if (e) e.preventDefault();
        const searchToUse = customSearch || states.search;
        const params = {
            ...defaultParams,
            search: searchToUse,
            pagination: initialPagination,
        };
        getAllBlockedCountryCodeData(params);
    };

    const handleTableFilterChange = (key, value, submit = false) => {
        setStates(old => {
            const updatedSearch = { ...old.search, [key]: value || '' };
            if (submit) {
                setTimeout(() => {
                    handleFiltersSubmit(null, updatedSearch);
                }, 0);
            }
            return { ...old, search: updatedSearch };
        });
    };

    const actions = [
        { label: 'Remove', action: () => handleModalOpen('remove') },
    ];

    useEffect(() => {
        getAllBlockedCountryCodeData(defaultParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (countryCodeBlockData && Array.isArray(countryCodeBlockData)) {
            setStates((old) => ({ ...old, data: countryCodeBlockData, pagination: pagination }));
        }
    }, [countryCodeBlockData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {

            if (!event.target.closest('.action-dropdown')) {
                handleCloseDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // TABLE COLUMNS SETUP AS PER DATA
    const columns = useMemo(() => [
        {
            accessor: 'createdDate',
            Header: 'Created At',
            minWidth: 200,
            style: {
                textAlign: 'center'
            },
            Filter: ({ filter, onChange }) => (
                <input
                    type="date"
                    name="createdDate"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('createdDate', value, true);
                    }}
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
        },
        {
            accessor: 'effectiveDate',
            Header: 'Effective At',
            minWidth: 200,
            style: {
                textAlign: 'center'
            },
            Filter: ({ filter, onChange }) => (
                <input
                    type="date"
                    name="effectiveDate"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('effectiveDate', value, true);
                    }}
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
        },
        {
            accessor: 'numericCode',
            Header: 'Numeric Code',
            minWidth: 140,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row.original.country?.numericCode,
            Filter: ({ filter, onChange }) => (
                <input
                    type="text"
                    name="numericCode"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('numericCode', value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
                            const value = e.target.value === '' ? undefined : e.target.value;
                            onChange(value);
                            handleTableFilterChange('numericCode', value, true);
                        }
                    }}
                    placeholder="Search by code"
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
            // filterable: false,
        },
        {
            accessor: 'status',
            Header: 'Status',
            minWidth: 140,
            style: {
                textAlign: 'center',
            },
            Cell: (row) => {
                return (
                    <BadgeComponent label="Blocked" type="error" />
                )
            },
            sortable: false,
            filterable: false,
        },
        {
            accessor: 'isoAlpha2',
            Header: 'Country Flag',
            minWidth: 140,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row.original.country?.isoAlpha2 ? (
                // <img
                //     src={`https://flagcdn.com/${row.original.country?.isoAlpha2.toLowerCase()}.svg`}
                //     alt={row.original.country?.name}
                //     style={{ border: '1px solid grey', width: '35px' }}
                // />
                <img
                    src={flagSvgs(`./${row.original.country?.isoAlpha2?.toLowerCase()}.svg`)}
                    alt={row.original.country?.name}
                    style={{ border: '1px solid var(--border-color)', width: '40px', height: 'auto', objectFit: 'cover' }}
                />
            ) : null,
            Filter: ({ filter, onChange }) => (
                <input
                    type="text"
                    name="code"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('code', value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
                            const value = e.target.value === '' ? undefined : e.target.value;
                            onChange(value);
                            handleTableFilterChange('code', value, true);
                        }
                    }}
                    placeholder="Search by code"
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
            filterable: false,
        },
        {
            accessor: 'name',
            Header: 'Country Name',
            minWidth: 180,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row.original.country?.name,
            Filter: ({ filter, onChange }) => (
                <input
                    type="text"
                    name="countryName"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('countryName', value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
                            const value = e.target.value === '' ? undefined : e.target.value;
                            onChange(value);
                            handleTableFilterChange('countryName', value, true);
                        }
                    }}
                    placeholder="Search by Country"
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
            // filterable: false,
        },
        {
            accessor: 'isoAlpha3',
            Header: 'Country Code ISO',
            minWidth: 160,
            style: {
                textAlign: 'center'
            },
            Cell: (row) => row.original.country?.isoAlpha3,
            Filter: ({ filter, onChange }) => (
                <input
                    type="text"
                    name="code"
                    value={filter ? filter.value : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange(value);
                        handleTableFilterChange('code', value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.keyCode === 13 || e.which === 13) {
                            const value = e.target.value === '' ? undefined : e.target.value;
                            onChange(value);
                            handleTableFilterChange('code', value, true);
                        }
                    }}
                    placeholder="Search by code"
                    style={{ width: '100%' }}
                />
            ),
            sortable: false,
            filterable: false,
        },
        {
            accessor: 'action',
            Header: 'Action',
            minWidth: 100,
            // style: {
            //     height: 45, display: 'flex', justifyContent: 'flex-end', alignItems: 'center'
            // },
            Cell: (row) => {
                return (
                    <div className="action-dropdown" style={{ position: 'absolute' }}>
                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row.original)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row.original)} />
                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row.original} close={handleCloseDropdown} actions={actions} />
                    </div>
                )
            },
            sortable: false,
            filterable: false,
        },
    ], [states.isDropdownOpen, states.selected]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthPageContainer $heightfull>
            {/* BACK BUTTON */}
            <BackButton />
            {/* PAGE HEAD */}
            <AuthPageHead>
                <div className="left">
                    <PageIcon className="page-icon" />
                    <h1>Global Country Block List</h1>
                    {/* <IconButtonComponent icon={ReloadSvg} outline size="small" disabled={loading} onClick={handleReload} className={loading && 'box-spin'} /> */}
                    <Breadcrumbs
                        items={[
                            { label: "Home", to: "/app/home" },
                            { label: PageLabel }
                        ]}
                        separator="›"
                        activeLastBreadcrumb={false}
                        size="small"
                        color={'var(--text-color-secondary)'}
                    />
                </div>

                <div className="right">
                    <ButtonComponent icon={PlusSvg} label="Block Countries" size="small" disabled={loading} onClick={handleModalOpen} />
                    {/* <IconButtonComponent icon={FilterSvg} outline onClick={handleToggleFilters} /> */}
                </div>
            </AuthPageHead>

            {/* FILTER */}
            {/* {
                searchFields && searchFields.length > 0 &&
                <FiltersComponent open={states.isFiltersOpen} searchFields={searchFields} handleChange={handleChangeFilters} handleSubmit={handleFiltersSubmit} />
            } */}

            {/* LOADING STATE */}
            {
                loading &&
                <LoaderComponent />
            }

            {/* EMPTY STATE */}
            {/* {
                !loading && states.data && states.data.length === 0 &&
                <EmptyTableComponent text='No data found!' />
            } */}

            {/* PAGE CONTENT */}
            {
               <>
                   <PaginationComponent
                        page={states?.pagination?.currentPage}
                        pages={states?.pagination?.totalPages || 1}
                        onPageChange={(e) => handlePagination(e)}
                        canPrevious={states?.pagination?.currentPage > 0}
                        canNext={states?.pagination?.currentPage < (states?.pagination?.totalPages - 1)}
                    />
                    <ReactTableWrapper className="box-shadow custom-scrollbar">
                        {/* <TableComponent rows={states.data} columns={columns} icon={BadgeSvg} />
                    <PaginationComponent data={states.pagination} handleChange={(e) => handlePagination(e)} /> */}
                        <ReactTableWrappers>
                            <ReactTable
                                manual
                                loading={loading}
                                data={states?.data}
                                columns={columns}
                                page={states?.pagination?.currentPage || 0}
                                pageSize={states?.pagination?.pageSize || 20}
                                pages={states?.pagination?.totalPages || 1}
                                filterable={true}
                                showPagination={false}
                                showPageJump={true}
                                showPageSizeOptions={false}
                                showPaginationTop={true}
                                showPaginationBottom={false}
                                onPageChange={(e) => handlePagination(e)}
                                minRows={0}
                            // defaultFilterMethod={(filter, row, column) => {
                            //     return true;
                            // }}
                            // onFilteredChange={(filtered, column, value) => {
                            //     const searchCriteria = transformToObject(filtered);
                            //     setComponentState((prevState) => ({
                            //         ...prevState,
                            //         searchCriteria,
                            //         tableLoading: true,
                            //     }));

                            //     handleOnSearch(searchCriteria); // Trigger handle search
                            // }}
                            />
                        </ReactTableWrappers>
                    </ReactTableWrapper>
                </>
            }

            {/* MODAL */}
            {
                states.isModalOpen &&
                <AddUpdateCountryCodeBlockModal data={form} label={`${states.isUpdate ? 'Update' : states.isView ? 'View' : 'Add'} Global Country Block List`} viewOnly={states.isView} update={states.isUpdate} close={handleModalClose} handleFormSubmit={handleFormSubmit} slide />
            }
            {
                states.isConfirmModal &&
                <ConfirmationModalComponent
                    type='warning'
                    label='Remove Country'
                    text={`Are you sure you want to remove Country (${states?.selected?.country?.name}) from blocked list?`}
                    close={handleModalClose}
                    actions={[
                        { label: 'No', type: 'button', outline: true, action: handleModalClose },
                        { label: 'Yes', type: 'button', action: handleRemoveSubmit }
                    ]}
                />
            }
        </AuthPageContainer>
    );
};

export default GlobalCountryBlock;